// Pure helper for the embed's /api/public/shop-config fetch.
//
// Pulled out of src/embed/index.ts so vitest can exercise the
// three-state return shape without standing up the embed's
// document.currentScript globals. The embed module imports this
// helper + wraps it with API_ORIGIN + SHOP_SLUG.
//
// Three return shapes drive the embed's render branch:
//   - object → shop resolved, render the form with that config
//   - null   → shop unknown / inactive (404). Render the "this
//              shop isn't available" panel — never the form.
//   - {}     → transient network error. Render the form with
//              defaults so the merchant isn't dead-pixeled when the
//              endpoint blips but the shop is actually fine.

export interface EmbedShopConfigLike {
  // Shape duck-types the embed's local ShopConfig interface. Kept
  // intentionally loose (Record<string, any>) so this module stays
  // a leaf import — no @/lib types pulled in.
  [key: string]: any;
}

/** Canonicalize a form id for URL + cache-key use: trimmed, lowercased,
 *  slug-shaped, and with the implicit `default` form collapsed to null.
 *  Returns null for anything that isn't a real, non-default form so both
 *  the fetch URL and the localStorage key stay byte-identical to the
 *  pre-multi-form single-form behavior (back-compat is non-negotiable). */
function normalizeFormId(formId?: string | null): string | null {
  const id = typeof formId === "string" ? formId.trim().toLowerCase() : "";
  if (!id || id === "default") return null;
  if (!/^[a-z0-9][a-z0-9-]*$/.test(id)) return null;
  return id;
}

export async function fetchEmbedShopConfig(opts: {
  apiOrigin: string;
  shopSlug: string;
  /** Multi-form (docs/MULTI_FORM.md §4.2): optional form id → the fetch
   *  appends `&form=<id>` so the endpoint returns that form's RESOLVED
   *  config. Absent / "default" / unknown → the default form (unchanged). */
  formId?: string;
  /** Override for tests — defaults to global fetch in production. */
  fetchImpl?: typeof fetch;
}): Promise<EmbedShopConfigLike | null> {
  const result = await fetchEmbedShopConfigWithVersion(opts);
  if (result === null) return null;
  return result.config;
}

/** Same fetch, but returns the version alongside the config so the
 *  caller can detect stale localStorage entries. Public endpoint's
 *  payload includes `version` (bumped on every merchant save). */
export async function fetchEmbedShopConfigWithVersion(opts: {
  apiOrigin: string;
  shopSlug: string;
  formId?: string;
  fetchImpl?: typeof fetch;
}): Promise<{ config: EmbedShopConfigLike; version: number } | null> {
  const f = opts.fetchImpl || fetch;
  // Multi-form: append `&form=<id>` only for a real, non-default form id.
  // A blank / "default" id is omitted so the URL (and therefore the CDN
  // cache key) stays byte-identical to the pre-multi-form request.
  const formQuery = normalizeFormId(opts.formId)
    ? `&form=${encodeURIComponent(normalizeFormId(opts.formId)!)}`
    : "";
  try {
    const r = await f(
      `${opts.apiOrigin}/api/public/shop-config?shop=${encodeURIComponent(opts.shopSlug)}${formQuery}`,
      { credentials: "omit" },
    );
    if (r.status === 404) return null;
    if (!r.ok) return { config: {}, version: 0 };
    const d = await r.json();
    const version = typeof d?.version === "number" ? d.version : 0;
    return { config: d?.config ?? {}, version };
  } catch {
    return { config: {}, version: 0 };
  }
}

// ── localStorage cache for shop-config ──────────────────────────────────────
// Repeat clicks within a session shouldn't pay the full network round
// trip every time. Cache the config for 5 minutes; embed boot reads
// the cache synchronously so the form can render immediately on a
// cache hit, then revalidates in the background. Merchant changes to
// /settings/forms still surface within 5 min of any storefront session.
//
// 404s are NOT cached — if a shop went inactive we want fresh checks
// on every load so the customer sees the inactive panel right away
// when the merchant uninstalls. {}/empty error returns also skip the
// write to avoid pinning a transient blip into the cache.
// Bump this prefix any time the shop_config public shape changes
// in a way that the embed bundle reads. v1 → v2 bump landed when
// v1.1 Catalog Rules replaced the v1.0 hide_prices fields with
// 12 new ones (hide_prices_enabled, hide_atc_*, etc.). Without
// the bump, every customer browser that hit the storefront in
// the previous 5 minutes served the v1.0 cached shape — the
// catalog-rules matcher read enabled=false (the new field is
// absent) and no-op'd silently while the merchant watched prices
// stubbornly render on a hide-prices-enabled product page.
const CONFIG_CACHE_PREFIX = "qb_cfg_v2_";
const CONFIG_CACHE_TTL_MS = 5 * 60 * 1000;

/** localStorage key for a shop's cached config. Multi-form: a real,
 *  non-default form id is suffixed so two forms on the same storefront
 *  don't clobber each other's cached composition (step order differs
 *  per form). The DEFAULT form (no / "default" id) keeps the EXACT
 *  pre-multi-form key `qb_cfg_v2_<shop>` — so existing cached entries
 *  and the shop-level catalog-rules/basket boot path (which reads with
 *  no form id) are unchanged. */
function cacheKey(shopSlug: string, formId?: string | null): string {
  const id = normalizeFormId(formId);
  return id
    ? `${CONFIG_CACHE_PREFIX}${shopSlug}::${id}`
    : `${CONFIG_CACHE_PREFIX}${shopSlug}`;
}

export interface CachedShopConfig {
  config: EmbedShopConfigLike;
  /** Monotonic version from the server (config_version column).
   *  Bumped on every merchant save. The embed compares this to a
   *  background revalidation's returned version — if newer, evicts
   *  the cached entry and re-renders. Legacy entries without
   *  version default to 0 so the next revalidation always wins. */
  version: number;
}

export function readCachedShopConfig(shopSlug: string, formId?: string | null): CachedShopConfig | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(shopSlug, formId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; config: EmbedShopConfigLike; version?: number };
    if (!parsed || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > CONFIG_CACHE_TTL_MS) return null;
    if (!parsed.config) return null;
    return { config: parsed.config, version: typeof parsed.version === "number" ? parsed.version : 0 };
  } catch {
    return null;
  }
}

export function writeCachedShopConfig(shopSlug: string, config: EmbedShopConfigLike, version = 0, formId?: string | null): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  // Skip writing empty / placeholder configs — those represent
  // transient network errors, not the real merchant config.
  if (!config || Object.keys(config).length === 0) return;
  try {
    window.localStorage.setItem(
      cacheKey(shopSlug, formId),
      JSON.stringify({ ts: Date.now(), config, version }),
    );
  } catch {
    // Quota / private mode / disabled — fall through silently.
  }
}

export function evictCachedShopConfig(shopSlug: string, formId?: string | null): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try { window.localStorage.removeItem(cacheKey(shopSlug, formId)); } catch { /* swallow */ }
}
